import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ConsentGate } from "@/components/ConsentGate";
import { useAuth } from "@/lib/auth";
import { resolveRouteAccess } from "@/lib/route-access";
import { Clock } from "lucide-react";

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

function PendingProvision() {
  const { signOut } = useAuth();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md animate-fade-in">
        <CardHeader className="text-center space-y-3">
          <div className="flex justify-center">
            <Clock className="h-10 w-10 text-muted-foreground" />
          </div>
          <CardTitle className="text-2xl">Account pending activation</CardTitle>
          <CardDescription>
            Your account has been created but isn&apos;t set up yet. Reach out to us and we&apos;ll get you activated.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Button asChild>
            <a href="mailto:support@fintective.ai">Contact support</a>
          </Button>
          <Button variant="outline" onClick={() => signOut()}>
            Sign in with a different account
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export function RouteGate({
  requireDeletionPending = false,
  allowDeletionPending = false,
  requireActiveSubscription = false,
}: {
  requireDeletionPending?: boolean;
  allowDeletionPending?: boolean;
  requireActiveSubscription?: boolean;
}) {
  const { user, loading, deletionStatus, agencyContext, notProvisioned, pendingConsent, confirmAcceptance, declineConsent } = useAuth();
  const location = useLocation();

  if (notProvisioned) {
    return <PendingProvision />;
  }

  const decision = resolveRouteAccess({
    loading,
    hasUser: !!user,
    deletionPending: deletionStatus?.pending === true,
    requireDeletionPending,
    allowDeletionPending,
    requireActiveSubscription,
    subscriptionStatus: agencyContext?.subscription_status,
    userRole: agencyContext?.user_role,
  });

  if (decision === "loading") {
    return <LoadingScreen />;
  }

  if (decision === "redirect_login") {
    return <Navigate to="/login" replace />;
  }

  if (decision === "redirect_dashboard") {
    return <Navigate to="/dashboard" replace />;
  }

  if (decision === "redirect_recovery") {
    return <Navigate to="/account/recover" replace state={{ from: location.pathname }} />;
  }

  if (decision === "redirect_billing") {
    return <Navigate to="/account?tab=billing" replace />;
  }

  // Existing user who signed up before the consent feature - block until they confirm
  if (pendingConsent) {
    return (
      <ConsentGate
        onConfirm={confirmAcceptance}
        onDecline={declineConsent}
        isNewUser={false}
      />
    );
  }

  return <Outlet />;
}
